import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { startCheckout } from "@/lib/billing";
import { FeedbackModal } from "./FeedbackModal";
import type { OpsApi, ConnState } from "../useOps";

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

function useClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const p = (n: number) => String(n).padStart(2, "0");
  return {
    time: `${p(now.getUTCHours())}:${p(now.getUTCMinutes())}:${p(now.getUTCSeconds())}`,
    date: `${p(now.getUTCDate())} ${MONTHS[now.getUTCMonth()]} ${now.getUTCFullYear()} · UTC`,
  };
}

/** Live trial countdown + conversion button — visible the whole free hour, ticking MM:SS.
 *  Checkout opens in a new tab; when it completes, auth-context's profile refresh flips
 *  status to 'active' and the chip (keyed on trialEndsAt of a pending row) disappears. */
function TrialChip({ endsAt }: { endsAt: number }) {
  const [busy, setBusy] = useState(false);
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const left = Math.max(0, endsAt - Date.now());
  const mm = String(Math.floor(left / 60000)).padStart(2, "0");
  const ss = String(Math.floor((left % 60000) / 1000)).padStart(2, "0");

  async function buy() {
    setBusy(true);
    const ok = await startCheckout();
    if (!ok) window.location.href = "mailto:info@earlywire.io?subject=Founding%20seat";
    setBusy(false);
  }

  return (
    <span className="trial-chip" title="Your free hour on the live desk — buy now to lock the founding rate before seats fill">
      <span className="tc-label">YOUR 1H TRIAL</span>
      <span className="tc-time">{mm}:{ss}</span>
      <button className="tc-buy" disabled={busy} onClick={() => void buy()}>
        {busy ? "OPENING…" : "BUY NOW"}
      </button>
    </span>
  );
}

const CONN_LABEL: Record<ConnState, string> = {
  connecting: "Connecting…",
  live: "Live · Streaming",
  reconnecting: "Reconnecting…",
  resynced: "Resynced · backfilled",
  simulated: "Simulated",
};

export function Header({ ops }: { ops: OpsApi }) {
  const { time, date } = useClock();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const { me, signOut } = useAuth();
  const email = me?.contactEmail ?? me?.email ?? "—";
  const live = ops.conn === "live" || ops.conn === "resynced";
  const connClass =
    ops.conn === "reconnecting" || ops.conn === "connecting" || ops.conn === "simulated"
      ? "reconnecting"
      : ops.conn === "resynced"
        ? "resynced"
        : "";

  return (
    <header id="head">
      <div className="brand" title="EARLYWIRE — OSINT-driven market signal intelligence">
        <div className="sigil" />
        <div>
          <div className="name">EARLYWIRE</div>
          <div className="sub">OSINT Signal Desk</div>
        </div>
      </div>
      <div className="hdiv" />
      <div className="region" title="Active monitoring viewport — all signals are global regardless of map position">
        <span className="k">Viewport</span>
        <span className="v">GLOBAL</span>
      </div>
      <div className={`conn ${connClass}`} title={`Data connection: ${CONN_LABEL[ops.conn]}`}>
        <span className="led" />
        <span className="txt">{CONN_LABEL[ops.conn]}</span>
      </div>
      <div className="clock" title="Current UTC time — all timestamps are UTC">
        <div className="t">{time}</div>
        <div className="d">{date}</div>
      </div>
      {me?.trialEndsAt != null && me.trialEndsAt > Date.now() && <TrialChip endsAt={me.trialEndsAt} />}
      <button className="mbtn" style={{ marginRight: 8 }} title="Report a bug privately, or jump to the public ideas board" onClick={() => setFeedbackOpen(true)}>
        FEEDBACK
      </button>
      <Link to="/account" className="acct" style={{ textDecoration: "none" }}>
        <div className="av">{email.charAt(0).toUpperCase()}</div>
        <div>
          <div className="em">{email}</div>
          <div className="pl" style={{ color: live ? "var(--green)" : "var(--amber)" }}>
            {me?.plan ? `${me.plan.toUpperCase()} · ${live ? "LIVE" : "OFFLINE"}` : "—"}
          </div>
        </div>
      </Link>
      <button
        className="mbtn"
        style={{ marginLeft: 8 }}
        onClick={() => void signOut()}
        title="Sign out"
      >
        EXIT
      </button>
      {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} />}
    </header>
  );
}
